import styled, { css } from 'styled-components';
import { Box } from '../../primitives/Box/Box.es.js';
import 'react';
import '../../primitives/Center/styled.es.js';
import '../../primitives/Group/Group.es.js';
import '../../primitives/Grid/Grid.es.js';
import '../../primitives/Level/Level.es.js';
import '../../primitives/Shelf/Shelf.es.js';
import '../../primitives/Portal/Portal.es.js';
import '../../primitives/Hidden/Hidden.es.js';
import '../../primitives/Media/Media.es.js';
import '../../primitives/Dialog/Dialog.es.js';
import '../../primitives/VisuallyHidden/VisuallyHidden.es.js';
import '../../primitives/TruncateByCharacters/TruncateByCharacters.es.js';
import '../../primitives/TruncateByLines/TruncateByLines.es.js';
import 'react-transition-group';
import '../../primitives/Popper/Popper.es.js';
import '../../primitives/DragHandle/DragHandle.es.js';
import '../../primitives/Pin/Pin.es.js';
import { variant, themeMode, color } from '../../utils/theme.es.js';

const InputDefaultStyles = {
  display: "flex",
  alignItems: "center",
  flex: "1 1 25%",
  zIndex: 1,
  position: "relative",
  lineHeight: "normal",
  width: "100%",
  minWidth: "75px",
  color: "inherit",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  appearance: "none",
  filter: "none",
  "&:focus": {
    outline: "none"
  },
  [`&::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration`]: {
    display: "none"
  },
  [`&:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active`]: {
    transition: "background-color 99999s ease-in-out 99999s, color 99999s ease-in-out 99999s"
  }
};
const inputDisabledStyles = variant({
  prop: "variant",
  variants: {
    outlined: themeMode({
      dark: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.800",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.100",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    }),
    filled: themeMode({
      dark: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.800",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.100",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    }),
    ghost: themeMode({
      dark: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    })
  }
});
const Input = styled(Box)`
    ${InputDefaultStyles};
    ${({ isMultiline, size }) => isMultiline && !!size && inputSizes}
    ${inputDisabledStyles}
`;
Input.defaultProps = { as: "input" };
Input.displayName = "Styled.Input";
const Addon = styled(Box)({
  display: "flex",
  alignItems: "center",
  flex: "0 1 auto",
  overflow: "hidden",
  zIndex: 2
});
const inputSizes = variant({
  prop: "size",
  compoundProp: "shape",
  variants: {
    sm: {
      fontSize: "sm",
      height: "36px",
      minHeight: "36px",
      [`& > ${Input}`]: {
        px: "sm"
      },
      rounded: {
        [`& > ${Input}`]: {
          px: "md"
        }
      }
    },
    md: {
      fontSize: "md",
      height: "48px",
      minHeight: "48px",
      [`& > ${Input}`]: {
        px: "md"
      },
      rounded: {
        [`& > ${Input}`]: {
          px: "lg"
        }
      }
    },
    lg: {
      fontSize: "lg",
      height: "64px",
      minHeight: "64px",
      [`& > ${Input}`]: {
        px: "lg"
      },
      rounded: {
        [`& > ${Input}`]: {
          px: "xl"
        }
      }
    }
  }
});
const inputShapes = variant({
  prop: "shape",
  variants: {
    default: {
      borderRadius: "sm"
    },
    rounded: {
      borderRadius: "round"
    }
  }
});
const inputVariants = variant({
  prop: "variant",
  variants: {
    outlined: themeMode({
      dark: {
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "neutral.600",
        backgroundColor: "transparent",
        color: "neutral.50",
        position: "relative",
        "input::placeholder": {
          color: "neutral.400"
        },
        "&:hover": {
          borderColor: "neutral.400"
        },
        "&:focus-within": {
          borderColor: "neutral.50"
        },
        isInvalid: {
          borderColor: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.800",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "neutral.300",
        background: "neutral.0",
        color: "neutral.900",
        "input::placeholder": {
          color: "neutral.400"
        },
        "&:hover": {
          borderColor: "neutral.600"
        },
        "&:focus-within": {
          borderColor: "neutral.900"
        },
        isInvalid: {
          borderColor: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.100",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    }),
    filled: themeMode({
      dark: {
        backgroundColor: "neutral.800",
        color: "neutral.50",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "transparent",
        "input::placeholder": {
          color: "neutral.400"
        },
        isInvalid: {
          borderColor: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.800",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        backgroundColor: "neutral.100",
        color: "neutral.900",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "transparent",
        "input::placeholder": {
          color: "neutral.400"
        },
        isInvalid: {
          borderColor: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          backgroundColor: "neutral.100",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    }),
    ghost: themeMode({
      dark: {
        background: "transparent",
        border: "1px solid transparent",
        color: "neutral.50",
        padding: 0,
        "input::placeholder": {
          color: "neutral.400"
        },
        isInvalid: {
          borderBottomColor: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      },
      light: {
        background: "transparent",
        border: "1px solid transparent",
        color: "neutral.900",
        padding: 0,
        "input::placeholder": {
          color: "neutral.500"
        },
        isInvalid: {
          color: "danger.500"
        },
        isDisabled: {
          color: "neutral.400",
          borderColor: "transparent",
          "&:hover": {
            borderColor: "transparent"
          }
        }
      }
    })
  }
});
const Wrapper = styled(Box)({
  display: "flex",
  justifyItems: "center",
  alignItem: "center",
  position: "relative",
  transition: "border-color 200ms ease-in, box-shadow 200ms ease-in"
}, css`
        &:focus-within {
            box-shadow: ${themeMode({ dark: color("neutral.50"), light: color("neutral.900") })} 0px 0px 0px 1px;
        }
    `, inputSizes, inputShapes, inputVariants, ({ isMultiline }) => isMultiline && {
  alignItems: "center",
  flexWrap: "wrap",
  minHeight: "48px",
  height: "auto"
});
Wrapper.displayName = "Styled.InputWrapper";

export { Addon, Input, InputDefaultStyles, Wrapper, inputDisabledStyles, inputShapes, inputSizes, inputVariants };
//# sourceMappingURL=styled.es.js.map
